import React, { useEffect } from "react";
import styled from "styled-components";
import { InvoiceLineItem, InvoiceSectionItem } from "./InvoiceItems";
import { InvoicePrintablePage } from "./InvoicePrintablePage";
import {
	InvoiceInfo,
} from "./RegisterInvoice";

interface Props {
	language: string;
	printClicked: boolean;
	handlePrint: Function;
	invoiceInfo: InvoiceInfo;
}

const InvoicePrintablePages = ({
	language,
	printClicked,
	handlePrint,
	invoiceInfo
}: Props) => {
	function formatValue(value: number) {
		if (language === "en") {
			const formatter = new Intl.NumberFormat("en-US", {
				style: "currency",
				currency: "USD",
			});
			return formatter.format(value);
		} else {
			const formatter = new Intl.NumberFormat("fr-CA", {
				style: "currency",
				currency: "USD",
			});
			var temp = formatter.format(value);
			return temp.replace("US", "");
		}
	}	

	useEffect(() => {
		// open print dialogue after page content is populated
		if (printClicked && handlePrint) {
			handlePrint();
		}
	}, [printClicked, handlePrint]);

	return (
		<StyledRegisterInvoice>
			<InvoicePrintablePage
				language={language}
				FirstPage={true}
				number="1"
				invoiceData={invoiceInfo}
			>
				<InvoiceSectionItem 
					SectionTitle={language === "en" ? "Sales Summary" : "Sommaire des ventes"}>
					<InvoiceLineItem
						ItemAmountText={`${formatValue(
							invoiceInfo.salesSummary.wirelessTransactions
						)}`}
						ItemTitleText={language === "en" ? "Interac/Debit and Credit" : "Débit/Interac et crédit"}
						ItemSubTitleText={language === "en" ? "(Total the amounts for all credit cards and Interac from ALL End of Day Reports)" : "(Faites le total des montants de toutes les cartes de crédit et de débit/Interac de TOUS les rapports de fin de journée.)"}
					/>
					<InvoiceLineItem
						ItemAmountText={`+${formatValue(
							invoiceInfo.salesSummary.cash
						)}`}
						ItemTitleText={language === "en" ? "Cash" : "Argent comptant"}
					/>
					<InvoiceLineItem
						ItemAmountText={`+${formatValue(
							invoiceInfo.salesSummary.cheques
						)}`}
						ItemTitleText={language === "en" ? "Cheques" : "Chèques"}
					/>
					<InvoiceLineItem
						ItemAmountText={`+${formatValue(
							invoiceInfo.salesSummary.manualTransactions
						)}`}
						ItemTitleText={language === "en" ? "Manual Imprinter transactions" : "Transactions manuelles par imprimante"}
						ItemSubTitleText={language === "en" ? "(If applicable, include imprinter slips with payment)" : "(le cas échéant, joignez les bordereaux d'impression au paiement.)"}
 					/>					
					<InvoiceLineItem
						ItemAmountText={`${formatValue(
							invoiceInfo.salesSummary.totalSales
						)}`}
						ItemTitleText={language === "en" ? "Total Sales" : "Total des ventes"}
						Bolded={true}
					/>
					<InvoiceLineItem
						ItemAmountText={`-${formatValue(
							invoiceInfo.salesSummary.taxes
						)}`}
						ItemTitleText={language === "en" ? "Applicable taxes" : "Taxes applicables"}
					/>
					<InvoiceLineItem
						ItemAmountText={`${formatValue(
							invoiceInfo.salesSummary.netSales
						)}`}
						ItemTitleText={language === "en" ? "Net Sales" : "Ventes nettes"}
						Bolded={true}
						LastItem={true}
					/>
				</InvoiceSectionItem>
				<InvoiceSectionItem SectionTitle={language === "en" ? "Rewards Summary" : "Sommaire des récompenses"}>
					{!invoiceInfo.isBogoFair && <InvoiceLineItem
						ItemAmountText={`${formatValue(
							invoiceInfo.rewardsSummary.scholasticDollarsEligible
						)}`}
						ItemTitleText={language === "en" ? "Fair / Festival Scholastic Dollars Eligible" : "Dollars Scholastic admissibles pour le Festival"}
						ItemSubTitleText={language === "en" ? `Net Sales x ${(invoiceInfo.rewardsSummary.scholasticDollarsRate * 100).toFixed(0)}%` : `Ventes nettes x ${(invoiceInfo.rewardsSummary.scholasticDollarsRate * 100).toFixed(0)} %`}
						LastItem={invoiceInfo.rewardsSummary.secondFairProductBonus !== 0 || invoiceInfo.rewardsSummary.specialBonus !== 0 ? false : true}
					/>}
					{invoiceInfo.isBogoFair && <InvoiceLineItem
						ItemAmountText={`${formatValue(
							invoiceInfo.rewardsSummary.productEligible
						)}`}
						ItemTitleText={language === "en" ? "Fair / Festival Product Rewards Eligible" : "Récompenses en produits admissibles pour le Festival"}
						ItemSubTitleText={language === "en" ? `Net Sales x ${(invoiceInfo.rewardsSummary.productRate * 100).toFixed(0)}%` : `Ventes nettes x ${(invoiceInfo.rewardsSummary.productRate * 100).toFixed(0)} %`}
						LastItem={invoiceInfo.rewardsSummary.secondFairProductBonus !== 0 || invoiceInfo.rewardsSummary.specialBonus !== 0 ? false : true}
					/>}
					{invoiceInfo.rewardsSummary.secondFairProductBonus !== 0 && <InvoiceLineItem
						ItemAmountText={`${formatValue(
							invoiceInfo.rewardsSummary.secondFairProductBonus
						)}`}
						ItemTitleText={language === "en" ? "2nd Fair Product Bonus" : "Primes en produits pour un deuxième Festival"}
						LastItem={invoiceInfo.rewardsSummary.specialBonus !== 0 ? false : true}
					/>}
					{invoiceInfo.rewardsSummary.specialBonus !== 0 && <InvoiceLineItem
						ItemAmountText={`${formatValue(
							invoiceInfo.rewardsSummary.specialBonus
						)}`}
						ItemTitleText={language === "en" ? "Special Bonus" : "Prime spéciale"}
						LastItem={true}
					/>}
				</InvoiceSectionItem>
				<InvoiceSectionItem SectionTitle={language === "en" ? "Earnings Selection" : "Sélection des gains"}>
					<InvoiceLineItem
						ItemAmountText={`${formatValue(
							invoiceInfo.profitSelection.productTakenOffTheFair
						)}`}
						ItemTitleText={language === "en" ? "Earnings in product taken off the fair" : "Gains en produits pris au Festival"}
						LastItem={invoiceInfo.isBogoFair}
					/>
					{!invoiceInfo.isBogoFair && <InvoiceLineItem
						ItemAmountText={`${formatValue(
							invoiceInfo.profitSelection.scholasticDollars
						)}`}
						ItemTitleText={language === "en" ? "Earnings in Scholastic Dollars" : "Gains en dollars Scholastic"}
						LastItem={invoiceInfo.province === "QUEBEC"}
					/>}
					{invoiceInfo.province !== "QUEBEC" && !invoiceInfo.isBogoFair && <InvoiceLineItem
						ItemAmountText={`${formatValue(
							invoiceInfo.profitSelection.cash
						)}`}
						ItemTitleText={language === "en" ? "Earnings in cash" : "Gains en argent comptant"}
						LastItem={invoiceInfo.province !== "QUEBEC" && !invoiceInfo.isBogoFair}
					/>}
				</InvoiceSectionItem>
			</InvoicePrintablePage>
			<InvoicePrintablePage
				language={language}
				number="2"
				invoiceData={invoiceInfo}
				LastPage={true}
			>
				<InvoiceSectionItem SectionTitle={language === "en" ? "Additional Product Taken" : "Produits additionnels pris"}>
					<InvoiceLineItem
						ItemAmountText={`${formatValue(
							invoiceInfo.additionalProductTaken.existingScholasticDollarBalance
						)}`}
						ItemTitleText={language === "en" ? "Existing Scholastic Dollar balance" : "Solde des dollars Scholastic existants"}
					/>
					<InvoiceLineItem
						ItemAmountText={`-${formatValue(
							invoiceInfo.additionalProductTaken.additionalProductTakenOffTheFair
						)}`}
						ItemTitleText={language === "en" ? "Additional Product taken off the Fair" : "Produits additionnels pris au Festival"}
						LastItem={true}
					/>
				</InvoiceSectionItem>
				<InvoiceSectionItem SectionTitle={language === "en" ? "Amount Due" : "Montant dû"}>
					<InvoiceLineItem
						ItemAmountText={`${formatValue(
							invoiceInfo.amountDue.cash
						)}`}
						ItemTitleText={language === "en" ? "Cash" : "Argent comptant"}
					/>
					<InvoiceLineItem
						ItemAmountText={`+${formatValue(
							invoiceInfo.amountDue.cheques
						)}`}
						ItemTitleText={language === "en" ? "Cheques" : "Chèques"}
					/>
					<InvoiceLineItem
						ItemAmountText={`+${formatValue(
							invoiceInfo.amountDue.deliveryFee
						)}`}
						ItemTitleText={language === "en" ? "Delivery fee" : "Frais de livraison"}
					/>
					<InvoiceLineItem
						ItemAmountText={`-${formatValue(
							invoiceInfo.amountDue.profitInCash
						)}`}
						ItemTitleText={language === "en" ? "Earnings in cash" : "Gains en argent comptant"}
					/>
					<InvoiceLineItem
						ItemAmountText={`${formatValue(
							invoiceInfo.amountDue.amountDue
						)}`}
						ItemTitleText={language === "en" ? "Total Amount Due" : "Montant total dû"}
						SemiBold={true}
						LargeText={true}
						LastItem={true}
					/>
				</InvoiceSectionItem>
			</InvoicePrintablePage>
		</StyledRegisterInvoice>
	);
};

const StyledRegisterInvoice = styled.div`
	display: flex;
	flex-direction: column;
`;

export default InvoicePrintablePages;
